import "@/App.css";
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Analytics from "@/pages/Analytics";
import Garden from "@/pages/Garden";
import Coach from "@/pages/Coach";
import Squad from "@/pages/Squad";
import HowTo from "@/pages/HowTo";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Upgrade from "@/pages/Upgrade";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Sidebar from "@/components/Sidebar";
import Splash from "@/components/Splash";

function Shell({ children }) {
  return (
    <div className="min-h-screen grain">
      <Sidebar />
      <main className="md:ml-64 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto min-h-screen">{children}</main>
    </div>
  );
}

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0B07]">
      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function isPro(user) {
  return user && (user.has_access || user.role === "admin");
}

// Auth only (used for Profile, Upgrade, payment pages)
function Protected({ children, chrome = true }) {
  const { user, checked } = useAuth();
  if (!checked) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return chrome ? <Shell>{children}</Shell> : children;
}

// Auth + active subscription (or admin)
function PremiumProtected({ children }) {
  const { user, checked } = useAuth();
  if (!checked) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isPro(user)) return <Navigate to="/upgrade" replace />;
  return <Shell>{children}</Shell>;
}

// Auth + admin
function AdminProtected({ children }) {
  const { user, checked, refreshUser } = useAuth();
  const [verifying, setVerifying] = useState(true);
  useEffect(() => {
    let active = true;
    refreshUser().finally(() => { if (active) setVerifying(false); });
    return () => { active = false; };
  }, []);
  if (!checked || verifying) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return <Shell>{children}</Shell>;
}

function LoginRoute() {
  const { user, checked } = useAuth();
  if (!checked) return null;
  if (user) return <Navigate to="/" replace />;
  return <Auth />;
}

function App() {
  return (
    <AuthProvider>
      <Splash />
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginRoute />} />

          {/* Gated app */}
          <Route path="/" element={<PremiumProtected><Dashboard /></PremiumProtected>} />
          <Route path="/garden" element={<PremiumProtected><Garden /></PremiumProtected>} />
          <Route path="/coach" element={<PremiumProtected><Coach /></PremiumProtected>} />
          <Route path="/squad" element={<PremiumProtected><Squad /></PremiumProtected>} />
          <Route path="/analytics" element={<PremiumProtected><Analytics /></PremiumProtected>} />
          <Route path="/guide" element={<PremiumProtected><HowTo /></PremiumProtected>} />
          <Route path="/admin" element={<AdminProtected><Admin /></AdminProtected>} />

          {/* Always available to logged-in users */}
          <Route path="/profile" element={<Protected><Profile /></Protected>} />
          <Route path="/upgrade" element={<Protected chrome={false}><Upgrade /></Protected>} />
          <Route path="/payment/success" element={<Protected chrome={false}><PaymentSuccess /></Protected>} />
          <Route path="/payment/cancel" element={<Protected chrome={false}><PaymentCancel /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
